import React, { useState, useEffect } from 'react';
import { User, Package, RotateCcw, Truck, DollarSign, MapPin, Calendar, Phone, TrendingUp } from 'lucide-react';
import AppConfig from '../config/appConfig'; // path আপনার ফোল্ডার স্ট্রাকচার অনুযায়ী ঠিক করুন

const DeliveryManInfo = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [urlPhone, setUrlPhone] = useState('');

  const BASE_URL = `${AppConfig.DeliveryManURL}`; // আপনার Flask API সার্ভারের URL
  const API_KEY = 'jwABi-kWdAebP6mohvoQcuLiRnCMJPPpcPpGMIqEiv0';

  // Extract phone from URL and auto-fetch
  useEffect(() => {
    const path = window.location.pathname;
    const phoneMatch = path.match(/\/delivery-man\/(\d+)/);
    if (phoneMatch && phoneMatch[1]) {
      const phone = phoneMatch[1];
      setUrlPhone(phone);
      fetchDeliveryManInfo(phone);
    }
  }, []);

  const fetchDeliveryManInfo = async (phoneNumber) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BASE_URL}/api/delivery-man/info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber,
          api_key: API_KEY
        })
      });

      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
    <div className={`${bgColor} rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{label}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
        </div>
        <div className={`${color} bg-opacity-10 p-4 rounded-full`}>
          <Icon size={32} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading delivery man info...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Animated Header */}
        <div className="text-center mb-8">
          <div className="inline-block mb-4 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full blur-xl opacity-50 animate-pulse"></div>
            <div className="relative bg-white p-6 rounded-full shadow-xl">
              <Truck size={64} className="text-indigo-600 animate-bounce" />
            </div>
          </div>
          <h1 className="text-6xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
            ${AppConfig.CompanyName} Delivery Portal
          </h1>
          <p className="text-gray-700 text-xl font-medium">ডেলিভারি হিরো পোর্টাল</p>
          <p className="text-gray-600 text-lg mt-1">আপনার পরিশ্রম, আমাদের সম্মান ❤️</p>
        </div>

        {/* Motivational Banner */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
          
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
              <span className="text-4xl">🏆</span>
              ডেলিভারি হিরো, আপনাকে স্বাগতম!
            </h2>
            <div className="grid md:grid-cols-2 gap-6 text-lg leading-relaxed">
              <div className="space-y-3">
                <p className="flex items-start gap-3">
                  <span className="text-2xl">💪</span>
                  <span>আপনার প্রতিটি শ্রমকে <strong>{AppConfig.CompanyName}</strong> অনেক মূল্যায়ন করে। আপনার পরিশ্রমই আমাদের সাফল্যের মূল চাবিকাঠি।</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-2xl">💰</span>
                  <span>প্রতিটি সফল ডেলিভারিতে পাবেন <strong className="text-yellow-300 text-xl">৳১৫ কমিশন</strong> - আপনার উপার্জন, আপনার প্রাপ্য!</span>
                </p>
              </div>
              <div className="space-y-3">
                <p className="flex items-start gap-3">
                  <span className="text-2xl">🎁</span>
                  <span>বিশেষ অফার: আমাদের যেকোনো পণ্যে পাবেন <strong className="text-yellow-300 text-xl">৳১৫০ ছাড়</strong>! আপনার পরিবারের জন্য কিনুন সাশ্রয়ী দামে।</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-2xl">🌟</span>
                  <span>প্রতিটি পার্সেল ডেলিভারি মানে একজন কাস্টমারের হাসি - আপনিই সেই হাসির কারিগর। চালিয়ে যান, সাফল্য আপনার!</span>
                </p>
              </div>
            </div>
            <div className="mt-6 p-4 bg-white bg-opacity-20 rounded-2xl backdrop-blur-sm">
              <p className="text-center text-xl font-bold">
                🚀 "আপনার প্রতিটি পদক্ষেপ আমাদের এগিয়ে নিয়ে যাচ্ছে। ধন্যবাদ!" 🙏
              </p>
            </div>
          </div>
        </div>



        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-xl mb-8 shadow-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-6 rounded-full">
                  <User size={48} className="text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">{data.name}</h2>
                  <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-gray-600">
                    <div className="flex items-center gap-2">
                      <Phone size={18} />
                      <span className="font-medium">{data.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={18} />
                      <span>{data.area}</span>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-1">Total Earnings</p>
                  <p className="text-3xl sm:text-4xl font-bold text-green-600">৳{data.total_amount}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={Package}
                label="Total Deliveries"
                value={data.delivery}
                color="text-green-600"
                bgColor="bg-green-50"
              />
              <StatCard
                icon={RotateCcw}
                label="Returns"
                value={data.return}
                color="text-red-600"
                bgColor="bg-red-50"
              />
              <StatCard
                icon={Truck}
                label="Orders Received"
                value={data.order_rcv}
                color="text-blue-600"
                bgColor="bg-blue-50"
              />
              <StatCard
                icon={TrendingUp}
                label="Success Rate"
                value={`${data.order_rcv > 0 ? ((data.delivery / data.order_rcv) * 100).toFixed(1) : 0}%`}
                color="text-purple-600"
                bgColor="bg-purple-50"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <DollarSign className="text-green-600" size={24} />
                  Earnings Breakdown
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl">
                    <span className="text-gray-700 font-medium">Delivery Earnings</span>
                    <span className="text-green-600 font-bold text-lg">৳{(data.delivery * 15).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-red-50 rounded-xl">
                    <span className="text-gray-700 font-medium">Return Deductions</span>
                    <span className="text-red-600 font-bold text-lg">- ৳{(data.return * 15).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-indigo-100 rounded-xl border-2 border-indigo-300">
                    <span className="text-gray-800 font-bold">Net Total</span>
                    <span className="text-indigo-600 font-bold text-2xl">৳{data.total_amount}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Calendar className="text-indigo-600" size={24} />
                  Activity Information
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                    <p className="text-gray-800 font-semibold">{data.last_update}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Active Since</p>
                    <p className="text-gray-800 font-semibold">{data.created_at}</p>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-xl">
                    <p className="text-sm text-gray-600 mb-1">Performance Status</p>
                    <p className="text-indigo-600 font-bold text-lg">
                      {data.delivery > data.return ? '🌟 Excellent' : '⚠️ Needs Improvement'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment & Withdrawal Section */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg p-8 border-2 border-green-200">
              <div className="text-center mb-6">
                <div className="inline-block bg-green-100 p-4 rounded-full mb-4">
                  <DollarSign size={48} className="text-green-600" />
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-2">💰 পেমেন্ট উত্তোলন</h3>
                <p className="text-gray-600 text-lg">আপনার উপার্জন এখনই উত্তোলন করুন</p>
              </div>

              <div className="bg-white rounded-2xl p-6 mb-6 shadow-md">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
                  <div className="text-center sm:text-left">
                    <p className="text-gray-600 text-sm mb-1">বর্তমান ব্যালেন্স</p>
                    <p className="text-3xl sm:text-4xl font-bold text-green-600">৳{data.total_amount}</p>
                  </div>
                  <div className="bg-green-100 p-4 rounded-full">
                    <svg className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>

                {data.total_amount >= 200 ? (
                  <button
                    onClick={() => alert('Withdraw function will be implemented soon!')}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 px-6 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105 flex items-center justify-center gap-3"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    উইথড্র করুন
                  </button>
                ) : (
                  <div className="text-center">
                    <button
                      disabled
                      className="w-full bg-gray-300 text-gray-500 py-4 px-6 rounded-xl font-bold text-lg cursor-not-allowed flex items-center justify-center gap-3"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      উইথড্র লক করা আছে
                    </button>
                    <p className="text-red-600 font-medium mt-4 text-lg">
                      ⚠️ ন্যূনতম ৳২০০ টাকা হলে উইথড্র করতে পারবেন
                    </p>
                    <p className="text-gray-600 mt-2">
                      আরও প্রয়োজন: <span className="font-bold text-red-600">৳{(200 - data.total_amount).toFixed(2)}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">ন্যূনতম উত্তোলন</p>
                  <p className="text-green-600 font-bold text-lg sm:text-xl">৳২০০</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">প্রসেসিং সময়</p>
                  <p className="text-blue-600 font-bold text-lg sm:text-xl">২৪ ঘণ্টা</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">চার্জ</p>
                  <p className="text-purple-600 font-bold text-lg sm:text-xl">০% ফ্রি</p>
                </div>
              </div>

              <div className="mt-6 p-5 bg-blue-50 rounded-xl border-2 border-blue-200">
                <p className="text-gray-700 text-center text-sm sm:text-base">
                  <span className="font-bold text-blue-600">📝 নোট:</span> উইথড্র করার জন্য আপনার মোবাইল নম্বরে bKash/Nagad/Rocket এ টাকা পাঠানো হবে। যেকোনো সমস্যায় আমাদের সাথে যোগাযোগ করুন।
                </p>
              </div>
            </div>

            {/* Contact & Support Section */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mt-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">📞 যোগাযোগ ও সহায়তা</h2>
                <p className="text-gray-600">যেকোনো সমস্যা বা প্রশ্নের জন্য আমরা সবসময় আপনার পাশে আছি</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Phone Call */}
                <a 
                  href={`tel:${AppConfig.phoneNumber}`}
                  className="group relative overflow-hidden bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-2xl p-6 sm:p-8 text-white transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative z-10">
                    <div className="bg-white bg-opacity-20 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-4 group-hover:rotate-12 transition-transform duration-300">
                      <Phone size={28} className="sm:w-8 sm:h-8 animate-pulse" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">📞 কল করুন</h3>
                    <p className="text-green-100 mb-3 text-sm sm:text-base">তাৎক্ষণিক সহায়তার জন্য</p>
                    <p className="text-2xl sm:text-3xl font-bold tracking-wider">{AppConfig.phoneNumber}</p>
                    <p className="text-xs sm:text-sm text-green-100 mt-3">⏰ সকাল ৯টা - রাত ৯টা (প্রতিদিন)</p>
                  </div>
                </a>

                {/* WhatsApp */}
                <a 
                  href={`https://wa.me/${AppConfig.WhatsAppNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-2xl p-6 sm:p-8 text-white transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative z-10">
                    <div className="bg-white bg-opacity-20 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-4 group-hover:rotate-12 transition-transform duration-300">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 sm:w-8 sm:h-8 animate-pulse">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">💬 WhatsApp করুন</h3>
                    <p className="text-emerald-100 mb-3 text-sm sm:text-base">দ্রুত মেসেজ ও সাপোর্টের জন্য</p>
                    <p className="text-2xl sm:text-3xl font-bold tracking-wider">{AppConfig.WhatsAppNumber}</p>
                    <p className="text-xs sm:text-sm text-emerald-100 mt-3">💬 যেকোনো সময় মেসেজ করুন</p>
                  </div>
                </a>
              </div>

              <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200">
                <p className="text-center text-gray-700 text-sm sm:text-base">
                  <span className="font-bold text-lg">💡 টিপস:</span> আপনার ডেলিভারি সংক্রান্ত যেকোনো সমস্যা, পেমেন্ট, কমিশন বা অন্য যেকোনো বিষয়ে সাহায্যের জন্য আমাদের সাথে যোগাযোগ করুন। আমরা সর্বদা আপনার সেবায় নিয়োজিত! 🙌
                </p>
              </div>
            </div>
          </div>
        )}

        {!data && !error && !loading && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-5">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                <path fill="#6366f1" d="M45.7,-57.8C58.9,-46.4,69.1,-31.7,73.7,-15.1C78.3,1.5,77.3,20,69.5,35.4C61.7,50.8,47.1,63.1,30.7,69.5C14.3,75.9,-3.9,76.4,-21.4,71.5C-38.9,66.6,-55.7,56.3,-65.8,41.5C-75.9,26.7,-79.3,7.4,-76.4,-10.8C-73.5,-29,-64.3,-46.1,-50.7,-57.3C-37.1,-68.5,-19,-73.8,-1.4,-72C16.2,-70.2,32.5,-69.2,45.7,-57.8Z" transform="translate(100 100)" />
              </svg>
            </div>
            
            <div className="relative z-10">
              {/* Animated Delivery Man */}
              <div className="mb-6 relative inline-block">
                <div className="absolute inset-0 bg-indigo-200 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                <div className="relative">
                  {/* Delivery Man Animation */}
                  <svg width="200" height="200" viewBox="0 0 200 200" className="animate-float">
                    {/* Package */}
                    <rect x="85" y="120" width="30" height="25" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" rx="2" className="animate-bounce-slow">
                      <animate attributeName="y" values="120;118;120" dur="2s" repeatCount="indefinite" />
                    </rect>
                    <line x1="85" y1="132" x2="115" y2="132" stroke="#f59e0b" strokeWidth="2"/>
                    <line x1="100" y1="120" x2="100" y2="145" stroke="#f59e0b" strokeWidth="2"/>
                    
                    {/* Body */}
                    <rect x="80" y="80" width="40" height="50" fill="#6366f1" rx="5">
                      <animate attributeName="y" values="80;78;80" dur="2s" repeatCount="indefinite" />
                    </rect>
                    
                    {/* Head */}
                    <circle cx="100" cy="60" r="20" fill="#fbbf24">
                      <animate attributeName="cy" values="60;58;60" dur="2s" repeatCount="indefinite" />
                    </circle>
                    
                    {/* Cap */}
                    <ellipse cx="100" cy="50" rx="22" ry="8" fill="#ef4444">
                      <animate attributeName="cy" values="50;48;50" dur="2s" repeatCount="indefinite" />
                    </ellipse>
                    <rect x="93" y="44" width="14" height="8" fill="#dc2626" rx="2">
                      <animate attributeName="y" values="44;42;44" dur="2s" repeatCount="indefinite" />
                    </rect>
                    
                    {/* Eyes */}
                    <circle cx="93" cy="58" r="2" fill="#1f2937"/>
                    <circle cx="107" cy="58" r="2" fill="#1f2937"/>
                    
                    {/* Smile */}
                    <path d="M 93 65 Q 100 70 107 65" stroke="#1f2937" strokeWidth="2" fill="none" strokeLinecap="round">
                      <animate attributeName="d" values="M 93 65 Q 100 70 107 65;M 93 65 Q 100 68 107 65;M 93 65 Q 100 70 107 65" dur="3s" repeatCount="indefinite" />
                    </path>
                    
                    {/* Arms */}
                    <line x1="75" y1="90" x2="85" y2="110" stroke="#6366f1" strokeWidth="8" strokeLinecap="round">
                      <animate attributeName="x1" values="75;73;75" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="y1" values="90;88;90" dur="2s" repeatCount="indefinite" />
                    </line>
                    <line x1="125" y1="90" x2="115" y2="110" stroke="#6366f1" strokeWidth="8" strokeLinecap="round">
                      <animate attributeName="x1" values="125;127;125" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="y1" values="90;88;90" dur="2s" repeatCount="indefinite" />
                    </line>
                    
                    {/* Legs */}
                    <line x1="90" y1="130" x2="85" y2="155" stroke="#1f2937" strokeWidth="8" strokeLinecap="round">
                      <animate attributeName="y1" values="130;128;130" dur="2s" repeatCount="indefinite" />
                    </line>
                    <line x1="110" y1="130" x2="115" y2="155" stroke="#1f2937" strokeWidth="8" strokeLinecap="round">
                      <animate attributeName="y1" values="130;128;130" dur="2s" repeatCount="indefinite" />
                    </line>
                    
                    {/* Shoes */}
                    <ellipse cx="85" cy="158" rx="8" ry="4" fill="#1f2937"/>
                    <ellipse cx="115" cy="158" rx="8" ry="4" fill="#1f2937"/>
                  </svg>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-800 mb-2">তথ্য খুঁজুন</h3>
              <p className="text-gray-600 mb-4">
                আপনার পারফরমেন্স দেখতে URL এ ফোন নম্বর যুক্ত করুন
              </p>
              
              {/* Animated Stats Preview */}
              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="p-4 bg-green-50 rounded-xl">
                  <Package size={32} className="text-green-600 mx-auto mb-2 animate-bounce-slow" />
                  <p className="text-green-600 font-bold text-lg">Deliveries</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-xl">
                  <DollarSign size={32} className="text-yellow-600 mx-auto mb-2 animate-bounce-slow" style={{animationDelay: '0.2s'}} />
                  <p className="text-yellow-600 font-bold text-lg">Earnings</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-xl">
                  <TrendingUp size={32} className="text-indigo-600 mx-auto mb-2 animate-bounce-slow" style={{animationDelay: '0.4s'}} />
                  <p className="text-indigo-600 font-bold text-lg">Success</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default DeliveryManInfo;